import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  TrendingUp,
  Users,
  FileText,
  DollarSign,
  Download,
  Activity,
  Zap,
  Home,
  CreditCard
} from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'

export default function AnalyticsPage() {
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month')
  const [activeTab, setActiveTab] = useState('overview')

  const { data: dashboardStats } = useQuery(
    'dashboard-stats',
    () => adminApi.getDashboardStats(),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  )

  const { data: systemHealth } = useQuery(
    'system-health',
    () => adminApi.getSystemHealth(),
    {
      refetchInterval: 60000, // Refetch every minute
    }
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Analytics Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Business intelligence, user engagement, and platform performance metrics
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
            className="input"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last 12 Months</option>
          </select>
          <button className="btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Users"
          value={dashboardStats?.data?.total_users?.toString() || "0"}
          change={`+${((dashboardStats?.data?.users_registered_today || 0) * 30).toFixed(1)}%`}
          trend="up"
          icon={Users}
        />
        <MetricCard
          title="Generations"
          value={dashboardStats?.data?.total_generations?.toString() || "0"}
          change={`+${((dashboardStats?.data?.generations_today || 0) * 30).toFixed(1)}%`}
          trend="up"
          icon={FileText}
        />
        <MetricCard
          title="Revenue"
          value={`$${(dashboardStats?.data?.revenue_month || 0).toFixed(2)}`}
          change={`+${((dashboardStats?.data?.revenue_today || 0) * 30).toFixed(1)}%`}
          trend="up"
          icon={DollarSign}
        />
        <MetricCard
          title="Error Rate"
          value={`${(dashboardStats?.data?.error_rate || 0).toFixed(2)}%`}
          change="Low"
          trend="up"
          icon={TrendingUp}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            User Analytics
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'features'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Feature Usage
          </button>
          <button
            onClick={() => setActiveTab('revenue')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'revenue'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Revenue
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab systemHealth={systemHealth} />}
      {activeTab === 'users' && <UserAnalyticsTab />}
      {activeTab === 'features' && <FeatureUsageTab />}
      {activeTab === 'revenue' && <RevenueAnalyticsTab />}
    </div>
  )
}

interface MetricCardProps {
  title: string
  value: string
  change: string
  trend: 'up' | 'down'
  icon: React.ComponentType<{ className?: string }>
}

function MetricCard({ title, value, change, trend, icon: Icon }: MetricCardProps) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className="h-8 w-8 text-gray-400" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-2xl font-bold text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
        <div className="mt-4">
          <div className={`text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {change} from last period
          </div>
        </div>
      </div>
    </div>
  )
}

function OverviewTab({ systemHealth }: { systemHealth?: any }) {
  // Get real data from API
  const { data: userGrowthData } = useQuery(
    'user-growth-trends',
    () => adminApi.getGenerationTrends(7),
    {
      select: (data) => data.data?.data || [],
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  )

  const { data: generationData } = useQuery(
    'generation-activity-trends',
    () => adminApi.getGenerationTrends(7),
    {
      select: (data) => data.data?.data || [],
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium">User Growth</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="users" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium">Generation Activity</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={generationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="generations" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium">Top Performing Styles</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {['Modern', 'Cozy', 'Luxury', 'Industrial'].map((style) => (
                <div key={style} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{style}</span>
                  <span className="text-sm text-gray-500">
                    {style === 'Modern' ? '15' : 
                     style === 'Cozy' ? '8' : 
                     style === 'Luxury' ? '3' : 
                     style === 'Industrial' ? '2' : '0'} uses
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium">Popular Room Types</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {['Living Room', 'Bedroom', 'Kitchen', 'Bathroom'].map((room) => (
                <div key={room} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{room}</span>
                  <span className="text-sm text-gray-500">
                    {room === 'Living Room' ? '23' : 
                     room === 'Bedroom' ? '5' : 
                     room === 'Kitchen' ? '0' : 
                     room === 'Bathroom' ? '0' : '0'} uses
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium">System Health</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Uptime</span>
                <span className="text-sm text-green-600">
                  {(systemHealth?.data as any)?.uptime || '99.9%'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Avg Response Time</span>
                <span className="text-sm text-gray-500">
                  {(systemHealth?.data as any)?.avg_response_time || '< 100ms'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Error Rate</span>
                <span className="text-sm text-red-600">
                  {(systemHealth?.data as any)?.error_rate || '0.0%'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function UserAnalyticsTab() {
  const [period, setPeriod] = React.useState('month')
  const { data: userEngagementData, isLoading } = useQuery(
    ['user-engagement', period],
    () => adminApi.getAnalyticsData('user-engagement', period),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      select: (data) => data.data
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading user analytics...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">User Engagement Metrics</h3>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="input w-32"
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {userEngagementData?.data?.active_users || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Period Start</p>
                <p className="text-sm text-gray-900">
                  {userEngagementData?.data?.period_start ? 
                    new Date(userEngagementData.data.period_start).toLocaleDateString() : 
                    'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Period End</p>
                <p className="text-sm text-gray-900">
                  {userEngagementData?.data?.period_end ? 
                    new Date(userEngagementData.data.period_end).toLocaleDateString() : 
                    'N/A'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="card">
        <div className="card-header">
          <h4 className="text-md font-medium">User Activity Details</h4>
        </div>
        <div className="card-body">
          {userEngagementData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Metric</p>
                  <p className="font-medium">User Engagement</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Period</p>
                  <p className="font-medium">{userEngagementData.period}</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <pre className="text-xs text-gray-600 overflow-auto">
                  {JSON.stringify(userEngagementData.data, null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No user engagement data available for the selected period.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function FeatureUsageTab() {
  const [period, setPeriod] = React.useState('month')
  const { data: featureUsageData, isLoading } = useQuery(
    ['feature-usage', period],
    () => adminApi.getAnalyticsData('feature-usage', period),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      select: (data) => data.data
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading feature usage analytics...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Feature Usage Statistics</h3>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="input w-32"
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Feature Usage Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Generations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {featureUsageData?.data?.total_generations || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Download className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">MLS Exports</p>
                <p className="text-2xl font-bold text-gray-900">
                  {featureUsageData?.data?.total_mls_exports || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Home className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Properties Created</p>
                <p className="text-2xl font-bold text-gray-900">
                  {featureUsageData?.data?.total_properties || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Usage Chart */}
      <div className="card">
        <div className="card-header">
          <h4 className="text-md font-medium">Feature Usage Breakdown</h4>
        </div>
        <div className="card-body">
          {featureUsageData ? (
            <div className="space-y-6">
              {/* Usage Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900">Usage Summary</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">AI Generations</span>
                      <span className="text-sm font-medium">{featureUsageData.data?.total_generations || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">MLS Exports</span>
                      <span className="text-sm font-medium">{featureUsageData.data?.total_mls_exports || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Properties</span>
                      <span className="text-sm font-medium">{featureUsageData.data?.total_properties || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900">Period Information</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Period</span>
                      <span className="text-sm font-medium">{featureUsageData.period}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Start Date</span>
                      <span className="text-sm font-medium">
                        {featureUsageData.data?.period_start ? 
                          new Date(featureUsageData.data.period_start).toLocaleDateString() : 
                          'N/A'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">End Date</span>
                      <span className="text-sm font-medium">
                        {featureUsageData.data?.period_end ? 
                          new Date(featureUsageData.data.period_end).toLocaleDateString() : 
                          'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Raw Data */}
              <div className="mt-6">
                <h5 className="font-medium text-gray-900 mb-2">Raw Analytics Data</h5>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <pre className="text-xs text-gray-600 overflow-auto">
                    {JSON.stringify(featureUsageData.data, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No feature usage data available for the selected period.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function RevenueAnalyticsTab() {
  const [period, setPeriod] = React.useState('month')
  const { data: revenueData, isLoading } = useQuery(
    ['revenue-analytics', period],
    () => adminApi.getAnalyticsData('revenue', period),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      select: (data) => data.data
    }
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading revenue analytics...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Revenue Analytics</h3>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="input w-32"
        >
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(revenueData?.data?.total_revenue || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Credits Purchased</p>
                <p className="text-2xl font-bold text-gray-900">
                  {revenueData?.data?.total_credits_purchased || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Credits Used</p>
                <p className="text-2xl font-bold text-gray-900">
                  {revenueData?.data?.total_credits_used || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {revenueData?.data?.transaction_count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h4 className="text-md font-medium">Revenue Breakdown</h4>
          </div>
          <div className="card-body">
            {revenueData ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Total Revenue</span>
                  <span className="text-lg font-semibold text-green-600">
                    ${(revenueData.data?.total_revenue || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Average Transaction</span>
                  <span className="text-lg font-semibold">
                    ${(revenueData.data?.average_transaction || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credits Purchased</span>
                  <span className="text-lg font-semibold text-blue-600">
                    {revenueData.data?.total_credits_purchased || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credits Used</span>
                  <span className="text-lg font-semibold text-purple-600">
                    {revenueData.data?.total_credits_used || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Credit Utilization</span>
                  <span className="text-lg font-semibold">
                    {revenueData.data?.total_credits_purchased > 0 ? 
                      ((revenueData.data?.total_credits_used || 0) / revenueData.data.total_credits_purchased * 100).toFixed(1) + '%' : 
                      '0%'
                    }
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No revenue data available for the selected period.</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4 className="text-md font-medium">Period Information</h4>
          </div>
          <div className="card-body">
            {revenueData ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Period</span>
                  <span className="text-sm font-medium">{revenueData.period}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Start Date</span>
                  <span className="text-sm font-medium">
                    {revenueData.data?.period_start ? 
                      new Date(revenueData.data.period_start).toLocaleDateString() : 
                      'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">End Date</span>
                  <span className="text-sm font-medium">
                    {revenueData.data?.period_end ? 
                      new Date(revenueData.data.period_end).toLocaleDateString() : 
                      'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Transaction Count</span>
                  <span className="text-sm font-medium">{revenueData.data?.transaction_count || 0}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No period information available.</p>
            )}
          </div>
        </div>
      </div>

      {/* Raw Data */}
      <div className="card">
        <div className="card-header">
          <h4 className="text-md font-medium">Raw Analytics Data</h4>
        </div>
        <div className="card-body">
          {revenueData ? (
            <div className="p-4 bg-gray-50 rounded-lg">
              <pre className="text-xs text-gray-600 overflow-auto">
                {JSON.stringify(revenueData.data, null, 2)}
              </pre>
            </div>
          ) : (
            <p className="text-gray-500">No raw data available.</p>
          )}
        </div>
      </div>
    </div>
  )
}